import { naturalCompare } from './naturalSort.js';
import { joinPath, nfc, normFolder, normKey, resolveRelative } from './text.js';

export interface ResolvableNote {
  id: string;
  /** Ordnerpfad im Vault, z. B. `Räume` (leer = Wurzel). */
  folder: string;
  /** Dateiname ohne `.md`. */
  title: string;
}

export interface ResolvableFile {
  id: string;
  folder: string;
  /** Dateiname inkl. Endung. */
  name: string;
}

export interface ResolveCandidate {
  type: 'note' | 'file';
  id: string;
  /** Vault-Pfad (Notizen ohne `.md`). */
  path: string;
}

export interface Resolution extends ResolveCandidate {
  /** Alle Treffer, falls mehrdeutig (inkl. des gewählten). Leer, wenn eindeutig. */
  conflicts: ResolveCandidate[];
}

export interface Resolver {
  resolve(target: string, fromFolder?: string): Resolution | null;
  resolveNote(target: string, fromFolder?: string): Resolution | null;
  resolveFile(target: string, fromFolder?: string): Resolution | null;
  /** Ist der Titel innerhalb der Kampagne eindeutig? (für das Umschreiben beim Umbenennen) */
  isUniqueNoteTitle(title: string): boolean;
}

/** Endungen, bei denen ein Ziel zuerst als Datei gesucht wird. */
const FILE_EXT_RE = /\.[a-z][a-z0-9]{1,4}$/i;
const MD_EXT_RE = /\.md$/i;

interface Index {
  byPath: Map<string, ResolveCandidate>;
  byBase: Map<string, ResolveCandidate[]>;
  all: ResolveCandidate[];
}

function buildIndex(items: { cand: ResolveCandidate; base: string }[]): Index {
  const byPath = new Map<string, ResolveCandidate>();
  const byBase = new Map<string, ResolveCandidate[]>();
  for (const { cand, base } of items) {
    byPath.set(normKey(cand.path), cand);
    const k = normKey(base);
    const list = byBase.get(k);
    if (list) list.push(cand);
    else byBase.set(k, [cand]);
  }
  return { byPath, byBase, all: items.map((i) => i.cand) };
}

function pick(cands: ResolveCandidate[]): Resolution | null {
  if (cands.length === 0) return null;
  const sorted = [...cands].sort((a, b) => a.path.length - b.path.length || naturalCompare(a.path, b.path));
  const best = sorted[0]!;
  return { ...best, conflicts: sorted.length > 1 ? sorted : [] };
}

function lookup(idx: Index, target: string, fromFolder: string): Resolution | null {
  let t = target;
  const absolute = t.startsWith('/');
  t = t.replace(/^\/+/, '');
  if (!t) return null;
  if (!t.includes('/')) {
    return pick(idx.byBase.get(normKey(t)) ?? []);
  }
  // Pfadangabe: exakt ab Wurzel, dann relativ zum Ordner der Quelle, dann Suffix
  const exact = idx.byPath.get(normKey(normFolder(t)));
  if (exact) return { ...exact, conflicts: [] };
  if (absolute) return null;
  const rel = resolveRelative(fromFolder, t);
  if (rel !== null) {
    const r = idx.byPath.get(normKey(rel));
    if (r) return { ...r, conflicts: [] };
  }
  const suffix = '/' + normKey(normFolder(t));
  return pick(idx.all.filter((c) => normKey(c.path).endsWith(suffix)));
}

/**
 * Linkauflösung wie in Obsidian:
 * - Ziel ohne Ordner → Abgleich auf Dateinamen (ohne `.md`), Groß-/Kleinschreibung egal;
 *   mehrere Treffer → kürzester Pfad, Konflikt wird gemeldet.
 * - Ziel mit Ordner → exakter Pfad (ab Wurzel oder relativ), sonst Suffix-Abgleich.
 * - Unicode wird vor dem Vergleich NFC-normalisiert.
 */
export function createResolver(notes: readonly ResolvableNote[], files: readonly ResolvableFile[] = []): Resolver {
  const noteIdx = buildIndex(
    notes.map((n) => ({ cand: { type: 'note' as const, id: n.id, path: joinPath(n.folder, nfc(n.title)) }, base: n.title })),
  );
  const fileIdx = buildIndex(
    files.map((f) => ({ cand: { type: 'file' as const, id: f.id, path: joinPath(f.folder, nfc(f.name)) }, base: f.name })),
  );

  const clean = (target: string) => nfc(target).replace(/\\/g, '/').trim();

  const resolveNote = (target: string, fromFolder = '') => {
    const t = clean(target).replace(MD_EXT_RE, '');
    return lookup(noteIdx, t, fromFolder);
  };
  const resolveFile = (target: string, fromFolder = '') => lookup(fileIdx, clean(target), fromFolder);

  return {
    resolveNote,
    resolveFile,
    resolve(target, fromFolder = '') {
      const t = clean(target);
      if (!t) return null;
      if (MD_EXT_RE.test(t)) return resolveNote(t, fromFolder);
      if (FILE_EXT_RE.test(t)) return resolveFile(t, fromFolder) ?? resolveNote(t, fromFolder);
      return resolveNote(t, fromFolder) ?? resolveFile(t, fromFolder);
    },
    isUniqueNoteTitle(title) {
      return (noteIdx.byBase.get(normKey(title))?.length ?? 0) <= 1;
    },
  };
}
